import _extends from "@babel/runtime/helpers/extends";
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { mergeRefs, useIsAutofilled } from '@pattern-library/base';
import { ViewIcon, ViewOffIcon } from '@pattern-library/icon';
import { FormInputErrors, FormInputLabel } from '@pattern-library/form-input';
import { FormLabelPopover } from '@pattern-library/message-popover';
const propTypes = {
  /**
   * Message to use to alert the user when their input value is shown or hidden.
   * Can be a static string or a function that returns the message.
   */
  alertMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * Spaced string for additional classes to apply to input
   **/
  classes: PropTypes.string,
  /**
   * Spaced string for additional classes to apply to container
   */
  containerClasses: PropTypes.string,
  /**
   * Disables input and button
   **/
  disabled: PropTypes.bool,
  /**
   * The severity of the input's error state
   */
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * Error message(s) to display when `hasError` is enabled.
   * Note: Only include [phrasing content](https://developer.mozilla.org/en-US/docs/Web/HTML/Content_categories#phrasing_content)
   * in the error message since the content is rendered within a `<p>` tag.
   **/
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * Element ID used for input, and label's for attribute
   **/
  id: PropTypes.string.isRequired,
  /**
   * Additional classes for the input element
   */
  infoPopoverContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional props to pass to the message popover component
   */
  infoPopoverProps: PropTypes.object,
  /**
   * Additional pass-through props for the input element
   * @deprecated - input props should be passed as extra props
   */
  inputProps: PropTypes.object,
  /**
   * Adds autocomplete="new-password" attribute for password managers.
   **/
  isNewPassword: PropTypes.bool,
  /**
   * Enable to make the input required
   */
  isRequired: PropTypes.bool,
  /**
   * Flag to enable the input's error state
   **/
  hasError: PropTypes.bool,
  /**
   * Tooltip text for hiding
   **/
  hideText: PropTypes.string,
  /**
   * Text used in input's label
   **/
  label: PropTypes.string,
  /**
   * Input name
   **/
  name: PropTypes.string,
  /**
   * Appended to the text label when input is optional.
   */
  optionalInputLabel: PropTypes.string,
  /**
   * Text used as hint inside input
   **/
  placeholder: PropTypes.string,
  /**
   * Adds autocomplete="PleaseDoNot" attribute to indicate no autofill to browsers and password managers
   * ('off' is ignored by all major browsers now, and requires a non-standard value.)
   * Typically used on non-standard password fields (SSN, etc.) Don't use on normal password fields,
   * which should allow users to autocomplete. Differs from isNewPassword, which should indicate new password fields only.
   **/
  preventAutofill: PropTypes.bool,
  /**
   * Append asterisk to label if input is required
   */
  showRequiredAsterisk: PropTypes.bool,
  /**
   * Tooltip text for showing
   **/
  showText: PropTypes.string,
  /**
   * Text used as hint below input
   */
  subtext: PropTypes.node,
  /**
   * Flag to use the embedded label
   */
  useEmbeddedLabel: PropTypes.bool
};
const defaultProps = {
  alertMessage: _ref => {
    let {
      isHidden
    } = _ref;
    return `Your password is ${isHidden ? 'hidden' : 'shown'}`;
  },
  classes: '',
  containerClasses: '',
  disabled: false,
  errorLevel: 'error',
  errorMessage: null,
  hasError: false,
  hideText: 'Hide Password',
  infoPopoverContent: null,
  infoPopoverProps: {},
  isNewPassword: false,
  isRequired: true,
  label: 'Password',
  name: 'password',
  optionalInputLabel: '(optional)',
  preventAutofill: false,
  showRequiredAsterisk: false,
  showText: 'Show Password',
  useEmbeddedLabel: false,
  inputProps: {}
};
const PasswordReveal = props => {
  const [showPassword, setShowPassword] = useState(false);
  const [inputHasValue, setInputHasValue] = useState(false);
  const inputRef = useRef();
  const isAutofilled = useIsAutofilled(inputRef);
  const {
    alertMessage,
    classes,
    containerClasses,
    disabled,
    errorLevel,
    errorMessage,
    hasError,
    hideText,
    id,
    infoPopoverContent,
    infoPopoverProps,
    isNewPassword,
    isRequired,
    label,
    name,
    optionalInputLabel,
    placeholder,
    preventAutofill,
    showRequiredAsterisk,
    showText,
    subtext,
    useEmbeddedLabel,
    inputProps,
    ...restInputProps
  } = props;
  const {
    'aria-describedby': ariaDescribedBy,
    onChange,
    ...extraInputProps
  } = restInputProps;
  const classNames = `asc-form-input ${classes}`;
  const type = showPassword ? 'text' : 'password';
  const title = showPassword ? hideText : showText;
  const toggleState = () => {
    setShowPassword(prevState => !prevState);
  };
  let autoCompleteString;
  if (isNewPassword) {
    autoCompleteString = 'new-password';
  } else if (preventAutofill) {
    autoCompleteString = 'pleaseDontFill';
  }
  const hasInfoPopover = infoPopoverContent !== null && !useEmbeddedLabel;
  const ids = {
    help: [`${id}-help`, hasError && Boolean(errorMessage)],
    subtext: [`${id}-subtext`, Boolean(subtext)],
    popover: [`${id}-popover`, hasInfoPopover]
  };
  let passwordRevealAriaDescribedByIds = Object.keys(ids).filter(key => ids[key][1]).map(key => ids[key][0]);
  if (ariaDescribedBy) {
    passwordRevealAriaDescribedByIds = passwordRevealAriaDescribedByIds.concat(ariaDescribedBy);
  }
  const handleInputChange = event => {
    setInputHasValue(Boolean(event.target.value));
    if (typeof onChange === 'function') {
      onChange(event);
    }
  };
  const combinedInputProps = {
    ...extraInputProps,
    ...inputProps
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classnames('asc-form-group', 'asc-password-reveal', containerClasses, {
      'asc-form-group--with-embedded-label': useEmbeddedLabel,
      'asc-form-group--with-value': inputHasValue,
      'asc-form-group--with-autofill': isAutofilled,
      'is-disabled': disabled,
      [`asc-form-input--${errorLevel}`]: hasError
    })
  }, /*#__PURE__*/React.createElement(FormInputLabel, {
    inputId: id,
    isRequired: isRequired,
    label: label,
    optionalInputLabel: optionalInputLabel,
    showRequiredAsterisk: showRequiredAsterisk
  }), hasInfoPopover && /*#__PURE__*/React.createElement(FormLabelPopover, {
    messagePopoverId: ids.popover[0],
    messagePopoverContent: infoPopoverContent,
    messagePopoverProps: infoPopoverProps
  }), /*#__PURE__*/React.createElement("div", {
    className: "asc-password-reveal-input-wrapper asc-form-input"
  }, /*#__PURE__*/React.createElement("input", _extends({
    id: id,
    name: name,
    type: type,
    className: classNames,
    placeholder: placeholder,
    autoComplete: autoCompleteString,
    disabled: disabled,
    onChange: handleInputChange,
    required: isRequired,
    "aria-describedby": passwordRevealAriaDescribedByIds.length ? passwordRevealAriaDescribedByIds.join(' ') : null
  }, combinedInputProps, {
    ref: mergeRefs([inputRef, combinedInputProps?.ref])
  })), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: toggleState,
    disabled: disabled,
    title: title
  }, showPassword ? /*#__PURE__*/React.createElement(ViewOffIcon, {
    color: "current"
  }) : /*#__PURE__*/React.createElement(ViewIcon, {
    color: "current"
  }), /*#__PURE__*/React.createElement("span", {
    className: "asc-acc-hidden"
  }, title)), /*#__PURE__*/React.createElement("div", {
    "aria-live": "polite",
    className: "asc-acc-hidden"
  }, typeof alertMessage === 'function' ? alertMessage({
    isHidden: !showPassword
  }) : alertMessage)), /*#__PURE__*/React.createElement(FormInputErrors, {
    containerId: ids.help[0],
    errorLevel: errorLevel,
    errorMessage: errorMessage,
    hasError: hasError
  }), subtext && /*#__PURE__*/React.createElement("div", {
    id: ids.subtext[0],
    className: "asc-form-subtext"
  }, subtext));
};
PasswordReveal.propTypes = propTypes;
PasswordReveal.defaultProps = defaultProps;
export default PasswordReveal;